/**
 * Emitting Now SSE honesty.
 *
 * GET /iot/analytics/emitting-now is a real SSE door. EventSource does not
 * error on an open heartbeat (or hung CONNECTING) stream, so this panel
 * must not wait forever. After the wait: timeout copy + retry, or
 * "No live events". Do not invent emitting devices.
 */

export const EMITTING_NOW_WAIT_MS = 15_000;

export const EMITTING_NOW_SSE_PATH = '/iot/analytics/emitting-now';

export type EmittingNowPhase =
  | 'no_door'
  | 'waiting'
  | 'live'
  | 'empty'
  | 'timeout'
  | 'error';

export function nextEmittingNowPhase(input: {
  hasSseUrl: boolean;
  eventCount: number;
  streamOpen: boolean;
  streamError: boolean;
  timedOut: boolean;
}): EmittingNowPhase {
  if (!input.hasSseUrl) return 'no_door';
  if (input.eventCount > 0) return 'live';
  if (input.streamError) return 'error';
  if (input.timedOut) return input.streamOpen ? 'empty' : 'timeout';
  return 'waiting';
}

export function emittingNowEmptyCopy(phase: EmittingNowPhase): {
  title: string;
  body: string;
  retry: boolean;
} {
  switch (phase) {
    case 'no_door':
      return {
        title: 'No live events',
        body: 'This panel has no signed-in SSE stream. It does not invent emitting devices.',
        retry: false,
      };
    case 'empty':
      return {
        title: 'No live events',
        body:
          'The live SSE stream is open and no devices have emitted in this wait. ' +
          'This panel does not invent emitting devices.',
        retry: true,
      };
    case 'timeout':
      return {
        title: 'Live stream timed out',
        body:
          'No SSE events arrived before the wait ended. Retry, or this panel stays empty. ' +
          'It does not invent emitting devices.',
        retry: true,
      };
    case 'error':
      return {
        title: 'Live stream failed',
        body: 'The SSE door returned an error. Retry. This panel does not invent emitting devices.',
        retry: true,
      };
    case 'waiting':
      return {
        title: 'Waiting for live SSE events…',
        body: '',
        retry: false,
      };
    case 'live':
      return { title: '', body: '', retry: false };
  }
}
